import { genericToast } from '@/toast';
import { isLocal } from '@/utils/axleEnvironment';
import { graphQlPath } from '@/utils/axleUrls';
import { envVars } from '@/utils/envVars';
import {
    type ApolloLink,
    CombinedGraphQLErrors,
    type FieldPolicy,
    HttpLink,
    type Reference,
    ServerError,
} from '@apollo/client';
import { type InMemoryCacheConfig, createFragmentRegistry } from '@apollo/client/cache';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { ErrorLink } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import Cookies from 'js-cookie';

import { FieldPolicies } from './FieldPolicies';
import { authErrorLink } from './errorLink';
import { commonFragments } from './fragments';

if (isLocal) {
    loadDevMessages();
    loadErrorMessages();
}

// TODO(jessica): Use BatchHttpLink once server support is in place.
const baseHeaders: Record<string, string> = {
    'X-Axle-Client': 'PARTNER_DASHBOARD',
};

/**
 * Custom fetch function that reads the CSRF token fresh from cookies on each request.
 * This ensures the X-CSRFTOKEN header is always up-to-date, similar to how axios handles
 * CSRF tokens with the withXSRFToken option.
 */
const fetchWithCsrf: typeof fetch = (input, init) => {
    const csrfToken = Cookies.get(envVars.csrfCookieName);
    const headers = new Headers(init?.headers);

    // Set base headers
    Object.entries(baseHeaders).forEach(([key, value]) => {
        if (!headers.has(key)) {
            headers.set(key, value);
        }
    });

    // Set CSRF token if available
    if (csrfToken) {
        headers.set('X-CSRFTOKEN', csrfToken);
    }

    // Append the operation name as a query param so requests are identifiable in
    // the network inspector (e.g. `graphql/?op=GetPatient`). The backend routes on
    // the path and ignores the extra param.
    const url = withOperationName(input, init);

    return fetch(url, {
        ...init,
        headers,
    });
};

export type AxleToastLevel = 'error' | 'warning' | 'info' | 'success';

/**
 * Per-operation context flag. Set `toastOnError` on a query/mutation to opt that
 * operation into auto-toasting any GraphQL errors it returns. Optionally override
 * the level (defaults to 'error').
 *
 * Example:
 *   useMutation(MY_MUTATION, {
 *       context: { toastOnError: true },
 *   });
 */
export type AxleErrorToastContext = {
    toastOnError?: boolean;
    toastLevel?: AxleToastLevel;
};

/**
 * Merge AxleErrorToastContext into Apollo's DefaultContext so `context` on
 * useQuery/useMutation gets autocomplete and type-checking for these flags.
 */
declare module '@apollo/client' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    export interface DefaultContext extends AxleErrorToastContext {}
}

/**
 * The backend re-mints the csrftoken cookie on the same response it rejects for a
 * CSRF failure (self-heal), so a retried request right after this one succeeds.
 * Checks the response body rather than trusting status 403 alone, since other 403s
 * (e.g. permission-denied) aren't CSRF-related and shouldn't be retried.
 */
const isCsrfFailure = (error: ServerError): boolean => {
    try {
        const body = JSON.parse(error.bodyText) as {
            errors?: { extensions?: { code?: string } }[];
        };
        return body.errors?.[0]?.extensions?.code === 'CSRF_FAILED';
    } catch {
        return false;
    }
};

export const AXLE_ERROR_HANDLING_LINK = new ErrorLink(({ error, operation, forward }) => {
    // A dropped csrftoken cookie (cleared, evicted, blocked) 403s every request
    // until something re-mints it. The backend does that on this very failure, so
    // retry once with the fresh cookie instead of surfacing a crash.
    if (ServerError.is(error) && error.statusCode === 403 && isCsrfFailure(error)) {
        const { csrfRetried } = operation.getContext() as { csrfRetried?: boolean };
        if (!csrfRetried) {
            operation.setContext({ csrfRetried: true });
            return forward(operation);
        }
    }

    // Only auto-toast GraphQL errors (v3 behavior). In v4 network/protocol errors
    // also flow through here as `error`, so gate on CombinedGraphQLErrors.
    if (!CombinedGraphQLErrors.is(error)) {
        return;
    }
    const { toastOnError, toastLevel = 'error' } = operation.getContext() as AxleErrorToastContext;
    if (!toastOnError) {
        return;
    }
    error.errors.forEach((graphQLError) => {
        genericToast(graphQLError.message, toastLevel);
    });
});

/**
 * Pull the GraphQL `operationName`(s) out of the request body and append them as
 * an `op` query param on the request URL. Purely cosmetic — helps distinguish
 * otherwise-identical `graphql/` entries in the network inspector.
 *
 * Handles both single ops (`HttpLink`) and batched ops (`BatchHttpLink`, where the
 * body is a JSON array of operations). For batches the names are joined with `+`.
 */
const withOperationName = (input: RequestInfo | URL, init?: RequestInit): RequestInfo | URL => {
    if (typeof input !== 'string' || typeof init?.body !== 'string') {
        return input;
    }
    try {
        const parsed = JSON.parse(init.body) as
            | { operationName?: string }
            | { operationName?: string }[];
        const names = (Array.isArray(parsed) ? parsed : [parsed])
            .map((op) => op.operationName)
            .filter(Boolean);
        if (names.length === 0) {
            return input;
        }
        const separator = input.includes('?') ? '&' : '?';
        return `${input}${separator}op=${encodeURIComponent(names.join('+'))}`;
    } catch {
        return input;
    }
};

/**
 * Cache redirect for root Query fields of the form `entity(id: UUID!): Entity`.
 *
 * Apollo normalizes entities by `__typename:id`, but does NOT automatically link
 * root fields like `Query.patient(id: X)` to a cached `Patient:X` entry that
 * was populated by a different query (e.g. a list). This redirect tells Apollo
 * that `Query.<entity>(id: X)` should resolve from the normalized `Entity:X`
 * reference — allowing detail queries to hit the cache when a list query has
 * already populated the fields they select. Apollo falls back to the network
 * on partial cache hits (missing fields), so this is a safe optimization.
 */
const redirectToEntityByIdArg = (typename: string): FieldPolicy => ({
    read(_existing, { args, toReference }) {
        const id = args?.id as string | undefined;
        return toReference({ __typename: typename, id });
    },
});

/**
 * A request that never reaches the server rejects with a bare `TypeError`, which Apollo
 * passes on to the caller. A suspense query rethrows it during render, so a connection that
 * dropped for a second is handled as though the page had crashed. Retrying covers the case
 * where the connection is back by the time the next attempt goes out.
 *
 * A request torn down on purpose (unmount, navigation) rejects with a `DOMException` named
 * `AbortError` rather than a `TypeError`, so cancelling a request does not retry it.
 */
const isTransportError = (error: unknown): boolean => error instanceof TypeError;

/**
 * Only queries are retried. A mutation whose response was lost may already have been applied
 * on the server, and sending it again would apply it twice.
 */
const isQueryOperation = (operation: ApolloLink.Operation): boolean =>
    operation.query.definitions.some(
        (definition) =>
            definition.kind === 'OperationDefinition' && definition.operation === 'query',
    );

export const TRANSPORT_RETRY_LINK = new RetryLink({
    delay: { initial: 300, max: 3000, jitter: true },
    attempts: {
        // Counts the first request, so this is the original plus two retries.
        max: 3,
        retryIf: (error, operation) => isTransportError(error) && isQueryOperation(operation),
    },
});

// The error link must come before the terminating HTTP link so it can inspect
//  responses on their way back. See errorLink.ts.
// The retry link sits closest to the HTTP link so a retried request does not re-run the
//  links above it.
export const APOLLO_LINK = authErrorLink.concat(
    TRANSPORT_RETRY_LINK.concat(
        new HttpLink({
            uri: graphQlPath,
            credentials: 'include',
            fetch: fetchWithCsrf,
        }),
    ),
);

export const APOLLO_CACHE_CONFIG: InMemoryCacheConfig = {
    /**
     * Register common fragments globally so they can be used in queries
     * without explicitly importing and including them.
     */
    fragments: createFragmentRegistry(commonFragments),
    /**
     * By defining which types are possible for a given interface or union type,
     * we can define shared type policies for the interface or union rather than
     * have to define them on each type individually.
     */
    possibleTypes: {
        RecurrenceRule: ['ShiftBlockRecurrence', 'ShiftRecurrence', 'RequestToCreateShiftBlock'],
        ScheduleEvent: ['ShiftBlockOccurrence', 'ShiftOccurrence', 'TentativeShiftBlockOccurrence'],
    },
    /*
     * Policies affecting how Apollo handles raw data coming in to the cache
     * and the cached data coming out of the cacheare defined here.
     *
     * Important: Queries called with a fetchPolicy of `no-cache` skip all of this
     * logic, which may produce unexpected behavior. Most cases where someone may
     * think to use 'no-cache' are better-served by 'network-only' instead, which ensures
     * that the query is always pulled fresh from the server when mounting, but also
     * passes the result through these transformations.
     *
     * See: https://www.apollographql.com/docs/react/caching/cache-field-behavior
     */
    typePolicies: {
        Clinician: {
            fields: {
                createdAt: FieldPolicies.datetime,
                updatedAt: FieldPolicies.datetime,
                permissions: {
                    merge: true,
                },
            },
        },
        ClinicianAppointment: {
            fields: {
                createdAt: FieldPolicies.datetime,
                startDatetime: FieldPolicies.datetime,
                endDatetime: FieldPolicies.datetime,
            },
        },
        ClinicianReservation: {
            fields: {
                startDatetime: FieldPolicies.datetime,
                endDatetime: FieldPolicies.datetime,
            },
        },
        ClinicianSchedule: {
            fields: {
                startDate: FieldPolicies.datetime,
                endDate: FieldPolicies.datetime,
                shifts: {
                    merge: false,
                },
                shiftBlocks: {
                    merge: false,
                },
                tentativeShiftBlocks: {
                    merge: false,
                },
                clinicianAppointments: {
                    merge: false,
                },
                clinicianReservations: {
                    merge: false,
                },
            },
        },
        ClinicianVisitPaginatedList: {
            merge: true,
        },
        Communication: {
            fields: {
                sentAt: FieldPolicies.datetime,
            },
        },
        DateFulfillmentWindow: {
            merge: true,
            fields: {
                startDate: FieldPolicies.date,
                endDate: FieldPolicies.date,
            },
        },
        DateRecommendationItem: {
            fields: {
                date: FieldPolicies.date,
            },
        },
        DecisionSupportQuery: {
            // This node is merely organizational, so we can safely merge it.
            merge: true,
        },
        DisciplinePaginatedList: {
            // TODO(jessica): Replace this with a proper paginated list merge policy once implemented and tested.
            merge: true,
        },
        Document: {
            fields: {
                createdAt: FieldPolicies.datetime,
                updatedAt: FieldPolicies.datetime,
            },
        },
        EpisodeOfCare: {
            fields: {
                startDate: FieldPolicies.date,
                endDate: FieldPolicies.date,
            },
        },
        ExternalId: {
            keyFields: false,
        },
        IncompatibleDateResult: {
            fields: {
                date: FieldPolicies.date,
            },
        },
        OptionalDateWindow: {
            merge: true,
            fields: {
                startDate: FieldPolicies.date,
                endDate: FieldPolicies.date,
            },
        },
        OptionalTimeWindow: {
            merge: true,
            fields: {
                startDatetime: FieldPolicies.datetime,
                endDatetime: FieldPolicies.datetime,
            },
        },
        Order: {
            fields: {
                activeOccurrences: {
                    merge: false,
                },
                createdAt: FieldPolicies.datetime,
                updatedAt: FieldPolicies.datetime,
                discontinuedAt: FieldPolicies.datetime,
                frequency: {
                    merge: true,
                },
                prn: {
                    merge: true,
                },
            },
        },
        OrderPaginatedList: {
            merge: true,
        },
        OrganizationPaginatedList: {
            merge: true,
        },
        Patient: {
            fields: {
                dateOfBirth: FieldPolicies.date,
                createdAt: FieldPolicies.datetime,
                permissions: {
                    merge: true,
                },
                // 'merge:false' here means to not merge and instead replace the existing list of
                // preferred timeslots with the incoming list
                preferredTimeslots: {
                    merge: false,
                },
                updatedAt: FieldPolicies.datetime,
            },
        },
        PatientDecisionSupport: {
            // This node is merely organizational, so we can safely merge it.
            merge: true,
        },
        PatientPaginatedList: {
            merge: true,
        },
        Query: {
            fields: {
                featureFlags: {
                    read(
                        existing: Reference[] | undefined,
                        { args, toReference, canRead },
                    ): Reference[] | undefined {
                        // If we have a cached result for this exact set of keys, use it.
                        if (existing) {
                            return existing;
                        }

                        // Otherwise, try to resolve each requested flag from the
                        // normalized cache. If every flag is already cached (from a
                        // different batch), return them directly — no network request.
                        const keys = args?.keys as string[] | undefined;
                        if (!keys) {
                            return undefined;
                        }

                        const refs = keys.map((key) =>
                            toReference({ __typename: 'FeatureFlag', id: key }),
                        );

                        // If any ref is missing from cache, fall through to network.
                        if (refs.some((ref) => !canRead(ref))) {
                            return undefined;
                        }

                        return refs as Reference[];
                    },
                },
                confirmedAvailability: {
                    // 'merge:false' here means to not merge and instead replace the existing availability
                    // with the incoming availability. This is because availability can change between
                    // different queries with the same inputs over time, and the results are not themselves normalized.
                    merge: false,
                },
                scheduleChangeRequests: {
                    // We can safely merge this non-normalized object because it is merely organizational.
                    merge: true,
                },
                unconfirmedAvailability: {
                    // 'merge:false' here means to not merge and instead replace the existing availability
                    // with the incoming availability. This is because availability can change between
                    // different queries with the same inputs over time, and the results are not themselves normalized.
                    merge: false,
                },
                // 'merge:false' here means to replace the existing list of
                // viewable visit outcomes with the incoming list
                viewableVisitOutcomes: {
                    merge: false,
                },
                // 'merge:false' here means to replace the existing list of
                // viewable visit states with the incoming list
                viewableVisitStates: {
                    merge: false,
                },
                // Cache redirects for root `entity(id: UUID!)` lookups.
                // Allow detail queries to resolve from entities already populated
                // in the normalized cache by list queries. See `redirectToEntityByIdArg`.
                address: redirectToEntityByIdArg('Address'),
                careTeam: redirectToEntityByIdArg('CareTeam'),
                clinician: redirectToEntityByIdArg('Clinician'),
                clinicianAppointment: redirectToEntityByIdArg('ClinicianAppointment'),
                clinicianTeam: redirectToEntityByIdArg('ClinicianTeam'),
                discipline: redirectToEntityByIdArg('Discipline'),
                episodeOfCare: redirectToEntityByIdArg('EpisodeOfCare'),
                optionValue: redirectToEntityByIdArg('OptionValue'),
                order: redirectToEntityByIdArg('Order'),
                organization: redirectToEntityByIdArg('Organization'),
                patient: redirectToEntityByIdArg('Patient'),
                payer: redirectToEntityByIdArg('Payer'),
                qualification: redirectToEntityByIdArg('Qualification'),
                reservation: redirectToEntityByIdArg('Reservation'),
                service: redirectToEntityByIdArg('Service'),
                serviceArea: redirectToEntityByIdArg('ServiceArea'),
                serviceBundle: redirectToEntityByIdArg('ServiceBundle'),
                shiftBlockRecurrence: redirectToEntityByIdArg('ShiftBlockRecurrence'),
                shiftRecurrence: redirectToEntityByIdArg('ShiftRecurrence'),
                tagGroup: redirectToEntityByIdArg('TagGroup'),
                territory: redirectToEntityByIdArg('Territory'),
                tripTask: redirectToEntityByIdArg('TripTask'),
                user: redirectToEntityByIdArg('User'),
                visit: redirectToEntityByIdArg('Visit'),
                visitOrder: redirectToEntityByIdArg('VisitOrder'),
                visitRequest: redirectToEntityByIdArg('VisitRequest'),
            },
        },
        RecurrenceRule: {
            fields: {
                startDatetime: FieldPolicies.datetime,
                endDatetime: FieldPolicies.datetime,
                excludeDatetimes: FieldPolicies.datetime,
                untilDatetime: FieldPolicies.datetime,
                createdAt: FieldPolicies.datetime,
                updatedAt: FieldPolicies.datetime,
                deletedAt: FieldPolicies.datetime,
            },
        },
        Reservation: {
            fields: {
                startDatetime: FieldPolicies.datetime,
                expiryDatetime: FieldPolicies.datetime,
            },
        },
        ScheduleChangeRequests: {
            // We can safely merge this non-normalized object because it is merely organizational.
            merge: true,
        },
        ScheduleEvent: {
            fields: {
                createdAt: FieldPolicies.datetime,
                startDatetime: FieldPolicies.datetime,
                endDatetime: FieldPolicies.datetime,
            },
        },
        ShiftRecurrence: {
            fields: {
                mobileTerritories: {
                    merge: false,
                },
                virtualTerritories: {
                    merge: false,
                },
            },
        },
        ShiftRecurrenceQualificationRestriction: {
            fields: {
                startOffsetMinutes: Number,
                endOffsetMinutes: Number,
            },
        },
        ShiftRecurrenceServiceLimit: {
            keyFields: ['service', ['id']],
        },
        SyncRecord: {
            fields: {
                createdAt: FieldPolicies.datetime,
            },
        },
        TentativeRoutedVisit: {
            fields: {
                tentativeStartDatetime: FieldPolicies.datetime,
            },
        },
        TimeFulfillmentWindow: {
            merge: true,
            fields: {
                startDatetime: FieldPolicies.datetime,
                endDatetime: FieldPolicies.datetime,
            },
        },
        TimestampedCoordinates: {
            fields: {
                timestamp: FieldPolicies.datetime,
            },
        },
        TimeWindow: {
            fields: {
                start: FieldPolicies.datetime,
                end: FieldPolicies.datetime,
                startDatetime: FieldPolicies.datetime,
                endDatetime: FieldPolicies.datetime,
            },
        },
        Visit: {
            fields: {
                assignedClinicians: {
                    // 'merge:false' here means to not merge and instead replace the existing list of
                    // assigned clinicians with the incoming list
                    merge: false,
                },
                clinicianAppointments: {
                    // 'merge:false' here means to not merge and instead replace the existing list of
                    // appointments with the incoming list
                    merge: false,
                },
                createdAt: FieldPolicies.datetime,
                date: FieldPolicies.date,
                endDatetime: FieldPolicies.datetime,
                // This is a non-normalized object that is unique per visit, so we can safely merge it.
                contact: {
                    merge: true,
                },
                permissions: {
                    merge: true,
                },
                // 'merge:false' here means to replace the existing list of
                // preferred timeslots with the incoming list
                preferredTimeslots: {
                    merge: false,
                },
                startDatetime: FieldPolicies.datetime,
                tentativeDate: FieldPolicies.date,
                updatedAt: FieldPolicies.datetime,
            },
        },
        VisitAssignmentQuery: {
            // This node is merely organizational, so we can safely merge it. Without this, a write
            // to one of its recommendation fields replaces the whole node and drops the others,
            // so two queries reading different fields invalidate each other and refetch in turn.
            merge: true,
        },
        VisitishDateRecommendation: {
            fields: { date: FieldPolicies.date },
        },
        VisitOrder: {
            fields: {
                createdAt: FieldPolicies.datetime,
                fromDate: FieldPolicies.date,
                toDate: FieldPolicies.date,
                updatedAt: FieldPolicies.datetime,
            },
        },
        VisitPaginatedList: {
            merge: true,
        },
        VisitRequest: {
            fields: {
                appointments: {
                    merge: false,
                },
                tentativeDate: FieldPolicies.date,
                warnings: {
                    merge: false,
                },
            },
        },
        VisitRequestAppointment: {
            fields: {
                // 'merge:false' here means to not merge and instead replace the existing list of
                // services with the incoming list
                services: {
                    merge: false,
                },
            },
        },
        VisitRequestDateRecommendation: {
            fields: {
                date: FieldPolicies.date,
            },
        },
        VisitStateChange: {
            fields: {
                createdAt: FieldPolicies.datetime,
                updatedAt: FieldPolicies.datetime,
            },
        },
        VisitTimeslotPreference: {
            fields: {
                startDatetime: FieldPolicies.datetime,
                endDatetime: FieldPolicies.datetime,
            },
        },
    },
};
